import type { KnowledgeBaseDocument } from '@/types/supabase';
import type { LibrarianDocumentSynthesis } from '@/lib/librarian/synthesizeDocument';
import type {
  KnowledgeBaseVault,
  KnowledgeBaseVaultPage,
  KnowledgeBaseVaultRun,
  KnowledgeGraphNode,
  KnowledgeGraphRetrievalHit,
  LibrarianSearchResult,
} from '@/lib/librarian/types';
import {
  fetchKnowledgeGraphEdges,
  fetchKnowledgeGraphNodes,
  fetchVaultLinks,
  fetchVaultPages,
  fetchVaultRuns,
  matchKnowledgeGraphNodes,
  slugifyVaultSegment,
  upsertKnowledgeGraphEdge,
  upsertKnowledgeGraphNode,
} from '@/lib/librarian/api';
import { generateEmbedding } from '@/lib/embeddings';
import { supabase } from '@/integrations/supabase/client';

function excerpt(text: string | null | undefined, maxLength = 320) {
  const value = (text ?? '').replace(/\s+/g, ' ').trim();
  return value.slice(0, maxLength) || null;
}

function uniqueStrings(values: Array<string | null | undefined>) {
  return Array.from(new Set(values.filter((value): value is string => Boolean(value))));
}

export async function syncKnowledgeGraphForVault(vault: KnowledgeBaseVault, createdBy?: string | null) {

  const [pages, links] = await Promise.all([
    fetchVaultPages(vault.id),
    fetchVaultLinks(vault.id),
  ]);

  const pageNodes = new Map<string, KnowledgeGraphNode>();

  for (const page of pages) {
    const node = await upsertKnowledgeGraphNode({
      knowledgeBaseId: vault.knowledge_base_id,
      nodeKey: `page:${page.id}`,
      nodeType: 'page',
      label: page.title,
      slug: page.slug,
      summary: page.summary,
      contentExcerpt: excerpt(page.content_markdown, 500),
      vaultId: vault.id,
      vaultPageId: page.id,
      metadata: {
        path: page.path,
        pageType: page.page_type,
        sourceDocumentIds: page.source_document_ids,
      },
      createdBy,
    });

    pageNodes.set(page.id, node);
  }

  for (const link of links) {
    const fromNode = pageNodes.get(link.from_page_id);
    const toNode = link.to_page_id ? pageNodes.get(link.to_page_id) : null;

    if (!fromNode || !toNode) continue;

    await upsertKnowledgeGraphEdge({
      knowledgeBaseId: vault.knowledge_base_id,
      fromNodeId: fromNode.id,
      toNodeId: toNode.id,
      edgeType: 'wiki_link',
      weight: 1,
      evidence: link.link_text,
      metadata: {
        targetPath: link.target_path,
      },
      createdBy,
    });
  }
}

export async function backfillKnowledgeGraphForVault(vault: KnowledgeBaseVault, createdBy?: string | null) {
  await syncKnowledgeGraphForVault(vault, createdBy);

  const [pages, runs, graphNodes, documents] = await Promise.all([
    fetchVaultPages(vault.id),
    fetchVaultRuns(vault.id),
    fetchKnowledgeGraphNodes(vault.knowledge_base_id),
    (supabase as any)
      .from('knowledge_base_documents')
      .select('*')
      .eq('knowledge_base_id', vault.knowledge_base_id),
  ]);

  if (documents.error) throw documents.error;

  const documentRows = (documents.data ?? []) as KnowledgeBaseDocument[];
  const pageNodesByPageId = new Map<string, KnowledgeGraphNode>(
    graphNodes
      .filter((node) => node.node_type === 'page' && node.vault_page_id)
      .map((node) => [node.vault_page_id as string, node])
  );

  const sourceNodesByDocumentId = new Map<string, KnowledgeGraphNode>();
  for (const document of documentRows) {
    const sourceNode = await upsertKnowledgeGraphNode({
      knowledgeBaseId: vault.knowledge_base_id,
      nodeKey: `source:${document.id}`,
      nodeType: 'source',
      label: document.name,
      slug: slugifyVaultSegment(document.name),
      summary: null,
      contentExcerpt: excerpt(document.content, 500),
      vaultId: vault.id,
      sourceDocumentId: document.id,
      metadata: {
        fileType: document.file_type,
        size: document.size,
        sourceName: document.name,
      },
      createdBy,
    });

    sourceNodesByDocumentId.set(document.id, sourceNode);
  }

  const runNodesByRunId = new Map<string, KnowledgeGraphNode>();
  for (const run of runs) {
    const runNode = await upsertKnowledgeGraphNode({
      knowledgeBaseId: vault.knowledge_base_id,
      nodeKey: `run:${run.id}`,
      nodeType: 'run',
      label: `${run.run_type} · ${run.status}`,
      slug: `${run.run_type}-${run.id}`,
      summary: run.notes,
      contentExcerpt: run.notes,
      vaultId: vault.id,
      vaultRunId: run.id,
      metadata: {
        runType: run.run_type,
        status: run.status,
        outputPageIds: run.output_page_ids,
        inputDocumentIds: run.input_document_ids,
        createdAt: run.created_at,
      },
      createdBy,
    });

    runNodesByRunId.set(run.id, runNode);

    for (const documentId of run.input_document_ids) {
      const sourceNode = sourceNodesByDocumentId.get(documentId);
      if (!sourceNode) continue;

      await upsertKnowledgeGraphEdge({
        knowledgeBaseId: vault.knowledge_base_id,
        fromNodeId: runNode.id,
        toNodeId: sourceNode.id,
        edgeType: 'processed_source',
        weight: 1,
        evidence: sourceNode.label,
        metadata: { documentId },
        createdBy,
      });
    }

    for (const pageId of run.output_page_ids) {
      const pageNode = pageNodesByPageId.get(pageId);
      if (!pageNode) continue;

      await upsertKnowledgeGraphEdge({
        knowledgeBaseId: vault.knowledge_base_id,
        fromNodeId: runNode.id,
        toNodeId: pageNode.id,
        edgeType: 'generated_by_run',
        weight: 1,
        evidence: run.run_type,
        metadata: { pageId },
        createdBy,
      });
    }
  }

  for (const page of pages) {
    const pageNode = pageNodesByPageId.get(page.id);
    if (!pageNode) continue;

    for (const documentId of page.source_document_ids) {
      const sourceNode = sourceNodesByDocumentId.get(documentId);
      if (!sourceNode) continue;

      await upsertKnowledgeGraphEdge({
        knowledgeBaseId: vault.knowledge_base_id,
        fromNodeId: pageNode.id,
        toNodeId: sourceNode.id,
        edgeType: 'references_source',
        weight: page.page_type === 'source' ? 1 : 0.9,
        evidence: page.title,
        metadata: { pagePath: page.path },
        createdBy,
      });
    }
  }

  const conceptNodesBySlug = new Map<string, KnowledgeGraphNode>();
  for (const page of pages) {
    if (page.page_type !== 'topic' && page.page_type !== 'entity') continue;

    const conceptType = page.page_type;
    const conceptNode = await upsertKnowledgeGraphNode({
      knowledgeBaseId: vault.knowledge_base_id,
      nodeKey: `${conceptType}:${page.slug}`,
      nodeType: conceptType,
      label: page.title,
      slug: page.slug,
      summary: page.summary,
      contentExcerpt: excerpt(page.content_markdown, 320),
      vaultId: vault.id,
      vaultPageId: page.id,
      metadata: {
        path: page.path,
        sourceDocumentIds: page.source_document_ids,
        ...page.metadata,
      },
      createdBy,
    });

    conceptNodesBySlug.set(`${conceptType}:${page.slug}`, conceptNode);

    const pageNode = pageNodesByPageId.get(page.id);
    if (pageNode) {
      await upsertKnowledgeGraphEdge({
        knowledgeBaseId: vault.knowledge_base_id,
        fromNodeId: conceptNode.id,
        toNodeId: pageNode.id,
        edgeType: 'derived_from_page',
        weight: 0.85,
        evidence: page.title,
        metadata: { pagePath: page.path },
        createdBy,
      });
    }

    for (const documentId of page.source_document_ids) {
      const sourceNode = sourceNodesByDocumentId.get(documentId);
      if (!sourceNode) continue;

      await upsertKnowledgeGraphEdge({
        knowledgeBaseId: vault.knowledge_base_id,
        fromNodeId: sourceNode.id,
        toNodeId: conceptNode.id,
        edgeType: 'mentions',
        weight: 0.82,
        evidence: page.title,
        metadata: { pagePath: page.path, conceptType },
        createdBy,
      });
    }
  }

  for (const page of pages) {
    if (page.page_type !== 'topic' && page.page_type !== 'entity') continue;

    const fromKey = `${page.page_type}:${page.slug}`;
    const fromNode = conceptNodesBySlug.get(fromKey);
    if (!fromNode) continue;

    const relatedNames = page.page_type === 'topic'
      ? (Array.isArray(page.metadata.relatedEntities) ? page.metadata.relatedEntities : [])
      : (Array.isArray(page.metadata.relatedTopics) ? page.metadata.relatedTopics : []);

    for (const relatedName of relatedNames.filter((value): value is string => typeof value === 'string')) {
      const relatedType = page.page_type === 'topic' ? 'entity' : 'topic';
      const toNode = conceptNodesBySlug.get(`${relatedType}:${slugifyVaultSegment(relatedName)}`);
      if (!toNode) continue;

      await upsertKnowledgeGraphEdge({
        knowledgeBaseId: vault.knowledge_base_id,
        fromNodeId: fromNode.id,
        toNodeId: toNode.id,
        edgeType: 'related_to',
        weight: 0.8,
        evidence: `${page.title} ↔ ${relatedName}`,
        metadata: { relation: `${page.page_type}-${relatedType}` },
        createdBy,
      });
    }
  }
}

export async function syncKnowledgeGraphForIngest(input: {
  vault: KnowledgeBaseVault;
  run: KnowledgeBaseVaultRun;
  document: KnowledgeBaseDocument;
  synthesis: LibrarianDocumentSynthesis;
  createdBy?: string | null;
}) {
  const { vault, run, document, synthesis, createdBy } = input;

  await syncKnowledgeGraphForVault(vault, createdBy);

  const [graphNodes, pages] = await Promise.all([
    fetchKnowledgeGraphNodes(vault.knowledge_base_id),
    fetchVaultPages(vault.id),
  ]);

  const pageNodesByPageId = new Map<string, KnowledgeGraphNode>(
    graphNodes
      .filter((node) => node.node_type === 'page' && node.vault_page_id)
      .map((node) => [node.vault_page_id as string, node])
  );
  const pageByPath = new Map<string, KnowledgeBaseVaultPage>(pages.map((page) => [page.path, page]));

  const sourceNode = await upsertKnowledgeGraphNode({
    knowledgeBaseId: vault.knowledge_base_id,
    nodeKey: `source:${document.id}`,
    nodeType: 'source',
    label: document.name,
    slug: slugifyVaultSegment(document.name),
    summary: synthesis.summary,
    contentExcerpt: excerpt(document.content, 500),
    vaultId: vault.id,
    sourceDocumentId: document.id,
    metadata: {
      fileType: document.file_type,
      size: document.size,
      sourceName: document.name,
    },
    createdBy,
  });

  const runNode = await upsertKnowledgeGraphNode({
    knowledgeBaseId: vault.knowledge_base_id,
    nodeKey: `run:${run.id}`,
    nodeType: 'run',
    label: `${run.run_type} · ${run.status}`,
    slug: `${run.run_type}-${run.id}`,
    summary: run.notes,
    contentExcerpt: run.notes,
    vaultId: vault.id,
    vaultRunId: run.id,
    metadata: {
      runType: run.run_type,
      status: run.status,
      outputPageIds: run.output_page_ids,
      inputDocumentIds: run.input_document_ids,
      createdAt: run.created_at,
    },
    createdBy,
  });

  await upsertKnowledgeGraphEdge({
    knowledgeBaseId: vault.knowledge_base_id,
    fromNodeId: runNode.id,
    toNodeId: sourceNode.id,
    edgeType: 'processed_source',
    weight: 1,
    evidence: document.name,
    metadata: { documentId: document.id },
    createdBy,
  });

  for (const page of pages) {
    const pageNode = pageNodesByPageId.get(page.id);
    if (!pageNode) continue;

    if (page.source_document_ids.includes(document.id)) {
      await upsertKnowledgeGraphEdge({
        knowledgeBaseId: vault.knowledge_base_id,
        fromNodeId: pageNode.id,
        toNodeId: sourceNode.id,
        edgeType: 'references_source',
        weight: 0.95,
        evidence: document.name,
        metadata: { pagePath: page.path },
        createdBy,
      });
    }

    if (run.output_page_ids.includes(page.id)) {
      await upsertKnowledgeGraphEdge({
        knowledgeBaseId: vault.knowledge_base_id,
        fromNodeId: runNode.id,
        toNodeId: pageNode.id,
        edgeType: 'generated_by_run',
        weight: 1,
        evidence: run.run_type,
        metadata: { pagePath: page.path },
        createdBy,
      });
    }
  }

  const topicNodes = new Map<string, KnowledgeGraphNode>();
  for (const topic of synthesis.topics) {
    const slug = slugifyVaultSegment(topic.name);
    const node = await upsertKnowledgeGraphNode({
      knowledgeBaseId: vault.knowledge_base_id,
      nodeKey: `topic:${slug}`,
      nodeType: 'topic',
      label: topic.name,
      slug,
      summary: topic.summary,
      contentExcerpt: excerpt(topic.evidence.join(' '), 320),
      vaultId: vault.id,
      sourceDocumentId: document.id,
      metadata: {
        evidence: topic.evidence,
        relatedEntities: topic.relatedEntities,
      },
      createdBy,
    });

    topicNodes.set(topic.name.toLowerCase(), node);

    await upsertKnowledgeGraphEdge({
      knowledgeBaseId: vault.knowledge_base_id,
      fromNodeId: sourceNode.id,
      toNodeId: node.id,
      edgeType: 'mentions',
      weight: 0.9,
      evidence: topic.evidence[0] ?? topic.summary,
      metadata: { entityType: 'topic' },
      createdBy,
    });

    const topicPage = pageByPath.get(`topics/${slug}.md`);
    const topicPageNode = topicPage ? pageNodesByPageId.get(topicPage.id) : null;
    if (topicPageNode) {
      await upsertKnowledgeGraphEdge({
        knowledgeBaseId: vault.knowledge_base_id,
        fromNodeId: node.id,
        toNodeId: topicPageNode.id,
        edgeType: 'derived_from_page',
        weight: 0.85,
        evidence: topic.name,
        metadata: { pagePath: topicPage.path },
        createdBy,
      });
    }
  }

  const entityNodes = new Map<string, KnowledgeGraphNode>();
  for (const entity of synthesis.entities) {
    const slug = slugifyVaultSegment(entity.name);
    const node = await upsertKnowledgeGraphNode({
      knowledgeBaseId: vault.knowledge_base_id,
      nodeKey: `entity:${slug}`,
      nodeType: 'entity',
      label: entity.name,
      slug,
      summary: entity.summary,
      contentExcerpt: excerpt(entity.evidence.join(' '), 320),
      vaultId: vault.id,
      sourceDocumentId: document.id,
      metadata: {
        entityType: entity.type,
        evidence: entity.evidence,
        relatedTopics: entity.relatedTopics,
      },
      createdBy,
    });

    entityNodes.set(entity.name.toLowerCase(), node);

    await upsertKnowledgeGraphEdge({
      knowledgeBaseId: vault.knowledge_base_id,
      fromNodeId: sourceNode.id,
      toNodeId: node.id,
      edgeType: 'mentions',
      weight: 0.9,
      evidence: entity.evidence[0] ?? entity.summary,
      metadata: { entityType: entity.type },
      createdBy,
    });

    const entityPage = pageByPath.get(`entities/${slug}.md`);
    const entityPageNode = entityPage ? pageNodesByPageId.get(entityPage.id) : null;
    if (entityPageNode) {
      await upsertKnowledgeGraphEdge({
        knowledgeBaseId: vault.knowledge_base_id,
        fromNodeId: node.id,
        toNodeId: entityPageNode.id,
        edgeType: 'derived_from_page',
        weight: 0.85,
        evidence: entity.name,
        metadata: { pagePath: entityPage.path },
        createdBy,
      });
    }
  }

  for (const topic of synthesis.topics) {
    const topicNode = topicNodes.get(topic.name.toLowerCase());
    if (!topicNode) continue;

    for (const relatedEntity of topic.relatedEntities) {
      const entityNode = entityNodes.get(relatedEntity.toLowerCase());
      if (!entityNode) continue;

      await upsertKnowledgeGraphEdge({
        knowledgeBaseId: vault.knowledge_base_id,
        fromNodeId: topicNode.id,
        toNodeId: entityNode.id,
        edgeType: 'related_to',
        weight: 0.8,
        evidence: `${topic.name} ↔ ${relatedEntity}`,
        metadata: { relation: 'topic-entity' },
        createdBy,
      });
    }
  }

  for (const entity of synthesis.entities) {
    const entityNode = entityNodes.get(entity.name.toLowerCase());
    if (!entityNode) continue;

    for (const relatedTopic of entity.relatedTopics) {
      const topicNode = topicNodes.get(relatedTopic.toLowerCase());
      if (!topicNode) continue;

      await upsertKnowledgeGraphEdge({
        knowledgeBaseId: vault.knowledge_base_id,
        fromNodeId: entityNode.id,
        toNodeId: topicNode.id,
        edgeType: 'related_to',
        weight: 0.8,
        evidence: `${entity.name} ↔ ${relatedTopic}`,
        metadata: { relation: 'entity-topic' },
        createdBy,
      });
    }
  }

  const conceptNodes = [...topicNodes.values(), ...entityNodes.values()];
  for (let index = 0; index < conceptNodes.length; index += 1) {
    const current = conceptNodes[index];
    const next = conceptNodes[index + 1];
    if (!current || !next) continue;

    await upsertKnowledgeGraphEdge({
      knowledgeBaseId: vault.knowledge_base_id,
      fromNodeId: current.id,
      toNodeId: next.id,
      edgeType: 'co_mentions',
      weight: 0.45,
      evidence: document.name,
      metadata: { sourceDocumentId: document.id },
      createdBy,
    });
  }
}

export async function retrieveGraphAugmentedResults(knowledgeBaseId: string, query: string) {
  const embedding = await generateEmbedding(query);
  if (!embedding) return [] as KnowledgeGraphRetrievalHit[];

  const [matches, nodes, edges] = await Promise.all([
    matchKnowledgeGraphNodes({
      knowledgeBaseId,
      queryEmbedding: embedding,
      threshold: 0.18,
      count: 8,
    }),
    fetchKnowledgeGraphNodes(knowledgeBaseId),
    fetchKnowledgeGraphEdges(knowledgeBaseId),
  ]);

  if (matches.length === 0) return [];

  const nodeById = new Map<string, KnowledgeGraphNode>(nodes.map((node) => [node.id, node]));
  const edgesByNodeId = new Map<string, typeof edges>();

  for (const edge of edges) {
    edgesByNodeId.set(edge.from_node_id, [...(edgesByNodeId.get(edge.from_node_id) ?? []), edge]);
    edgesByNodeId.set(edge.to_node_id, [...(edgesByNodeId.get(edge.to_node_id) ?? []), edge]);
  }

  const scores = new Map<string, KnowledgeGraphRetrievalHit>();

  const pushHit = (hit: KnowledgeGraphRetrievalHit) => {
    const existing = scores.get(`${hit.source}:${hit.id}`);
    if (!existing || hit.score > existing.score) {
      scores.set(`${hit.source}:${hit.id}`, hit);
    }
  };

  for (const match of matches) {
    const { node, similarity } = match;

    if (node.vault_page_id) {
      pushHit({
        source: 'page',
        id: node.vault_page_id,
        score: similarity * 1.15,
        reason: `Graph semantic match on ${node.node_type}`,
        graphNodeType: node.node_type,
      });
    }

    if (node.source_document_id) {
      pushHit({
        source: 'source',
        id: node.source_document_id,
        score: similarity * 1.1,
        reason: `Graph semantic match on ${node.node_type}`,
        graphNodeType: node.node_type,
      });
    }

    const connectedEdges = edgesByNodeId.get(node.id) ?? [];
    for (const edge of connectedEdges) {
      const otherNodeId = edge.from_node_id === node.id ? edge.to_node_id : edge.from_node_id;
      const otherNode = nodeById.get(otherNodeId);
      if (!otherNode) continue;

      const weightedScore = similarity * (edge.weight || 1) * 0.82;
      const reason = `${node.label} connected via ${edge.edge_type.replace(/_/g, ' ')}`;

      if (otherNode.vault_page_id) {
        pushHit({
          source: 'page',
          id: otherNode.vault_page_id,
          score: weightedScore,
          reason,
          graphNodeType: node.node_type,
        });
      }

      if (otherNode.source_document_id) {
        pushHit({
          source: 'source',
          id: otherNode.source_document_id,
          score: weightedScore,
          reason,
          graphNodeType: node.node_type,
        });
      }
    }
  }

  return Array.from(scores.values()).sort((a, b) => b.score - a.score).slice(0, 8);
}

export async function hydrateGraphRetrievalHits(
  knowledgeBaseId: string,
  hits: KnowledgeGraphRetrievalHit[]
): Promise<LibrarianSearchResult[]> {
  const pageIds = uniqueStrings(hits.filter((hit) => hit.source === 'page').map((hit) => hit.id));
  const sourceIds = uniqueStrings(hits.filter((hit) => hit.source === 'source').map((hit) => hit.id));

  const [pageResponse, sourceResponse] = await Promise.all([
    pageIds.length > 0
      ? (supabase as any).from('knowledge_base_vault_pages').select('*').in('id', pageIds)
      : Promise.resolve({ data: [], error: null }),
    sourceIds.length > 0
      ? (supabase as any).from('knowledge_base_documents').select('*').eq('knowledge_base_id', knowledgeBaseId).in('id', sourceIds)
      : Promise.resolve({ data: [], error: null }),
  ]);

  if (pageResponse.error) throw pageResponse.error;
  if (sourceResponse.error) throw sourceResponse.error;

  const pageRows = (pageResponse.data ?? []) as any[];
  const sourceRows = (sourceResponse.data ?? []) as any[];
  const pageById = new Map<string, any>(pageRows.map((page) => [page.id, page]));
  const sourceById = new Map<string, any>(sourceRows.map((document) => [document.id, document]));

  const results: LibrarianSearchResult[] = [];

  for (const hit of hits) {
    if (hit.source === 'page') {
      const page = pageById.get(hit.id);
      if (!page) continue;

      results.push({
        kind: 'vault',
        id: page.id,
        title: page.title,
        path: page.path,
        pageType: page.page_type,
        content: page.content_markdown,
        similarity: hit.score,
        sourceDocumentIds: Array.isArray(page.source_document_ids) ? page.source_document_ids : [],
        retrievalReason: hit.reason,
        graphNodeType: hit.graphNodeType,
      });
      continue;
    }

    const source = sourceById.get(hit.id);
    if (!source) continue;

    results.push({
      kind: 'source',
      id: source.id,
      title: source.name,
      content: source.content,
      similarity: hit.score,
      retrievalReason: hit.reason,
      graphNodeType: hit.graphNodeType,
    });
  }

  return results;
}